import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useMemo, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {boxedInputColorReaders} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {PopoverProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverTypes';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {AnswerModel} from '../../../../models/api/answerModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {AnswerNameAlreadyExistsError} from '../../../../store/data/answers/answerActions';
import {OkCancelDialog} from '../../../common/dialogs/okCancelDialog';
import {AnswerFolderDropdownBoxContainer} from '../../../common/dropdowns/answerFolders/answerFolderDropdownBoxContainer';
import {NamespaceDropdownBoxContainer} from '../../../common/dropdowns/namespace/namespaceDropdownBoxContainer';

/*
 * Props.
 */

export interface AnswerDialogBaseProps extends PopoverProps {
  onCreateClick: AsyncHandlerOf<AnswerDialogBaseValues, AnswerModel>;
  children: RendererOf<AnswerDialogBodyProps>;
  namespace: string;
  hasValidBodyValue: boolean;
  hasBodyChanged: boolean;
}

export interface AnswerDialogBodyProps {
  boxClassName?: string;
}

export interface AnswerDialogValues {
  name: string;
  namespace: string;
  folderId: number | undefined;
  body: string;
}

type AnswerDialogBaseValues = Omit<AnswerDialogValues, 'body'>;

type ErrorScope = 'name' | 'global';

/*
 * Style.
 */

interface BodyWrapperProps {
  className?: string;
  children: RendererOf<AnswerDialogBodyProps>;
}

const BodyWrapper: FC<BodyWrapperProps> = ({className, children}) => {
  const bodyProps = useMemo<AnswerDialogBodyProps>(() => ({boxClassName: className}), [className]);

  return <>{children(bodyProps)}</>;
};

const StyledBodyWrapper = styled(BodyWrapper)`
  /* Scroll instead of autosize. */
  overflow: auto;
  /* To fit in the smallest popout composer. */
  height: 150px;

  /* Turn off the default flex on the x-axis to make the editor line wrap properly. */
  display: initial;
`;

const StyledFieldDiv = styled.div`
  & + & {
    margin-top: 9px;
  }
`;

const StyledErrorDiv = styled.div`
  font-weight: ${fontWeights.medium};
  color: ${boxedInputColorReaders.textError};
`;

/*
 * Component.
 */

export const AnswerDialogBase: FC<AnswerDialogBaseProps> = (props) => {
  const {
    onCreateClick,
    onRequestClose,
    children,
    hasValidBodyValue,
    namespace: resourceNamespace,
    ...popoverProps
  } = props;
  const [name, setName] = useState('');
  const [parentFolderId, setParentFolderId] = useState<number | undefined>(undefined);
  const [namespace, setNamespace] = useState('');

  const [error, setError]: [Error | undefined, (error: Error) => void] = useState();
  const canCreate = name.trim().length > 0 && namespace.trim().length > 0 && hasValidBodyValue;

  const onCreateClickWithErrorHandled = async () => {
    const answerCreatedPromise = onCreateClick({
      name,
      folderId: parentFolderId,
      namespace,
    });

    try {
      const answerCreated: AnswerModel = await answerCreatedPromise;
      trackInterfaceEvent(InterfaceEventsEnum.CANNED_ANSWER_CREATE, {
        hasBodyChanged: props.hasBodyChanged,
        id: answerCreated.id,
        canned_answer_folder_id: answerCreated.folderId,
        if_nested_folder: Boolean(answerCreated.folderId),
      });

      if (onRequestClose) {
        onRequestClose();
      }

      return answerCreatedPromise;
    } catch (err) {
      return setError(err);
    }
  };

  const [errorScope, errorNode] = getError(error);

  return (
    <OkCancelDialog
      {...popoverProps} /* eslint-disable-line react/jsx-props-no-spreading */
      title={<Trans id="answer-create-header">Create message template</Trans>}
      okButtonLabel={<Trans id="answer-create-ok">Create</Trans>}
      onOkClick={onCreateClickWithErrorHandled}
      isOkDisabled={!canCreate}
      onRequestClose={onRequestClose}
    >
      <StyledFieldDiv>
        <TextBox
          label={<Trans id="answer-create-field-name">Name</Trans>}
          value={name}
          onChange={(text) => setName(text)}
          hasError={errorScope === 'name'}
          errorMessage={errorNode}
          shouldAutoFocus
        />
      </StyledFieldDiv>
      <StyledFieldDiv>
        <NamespaceDropdownBoxContainer
          placeholder={<Trans id="answer-create-access-placeholder">Select a team or make it private</Trans>}
          resourceNamespace={resourceNamespace}
          selectedNamespace={namespace}
          onNamespaceSelect={setNamespace}
          permission={PermissionsEnum.EDIT_CANNED_ANSWER}
        />
      </StyledFieldDiv>
      <StyledFieldDiv>
        <AnswerFolderDropdownBoxContainer
          selectedFolderId={parentFolderId}
          onFolderSelect={setParentFolderId}
          resourceNamespace={resourceNamespace}
          selectedNamespace={namespace}
          permission={PermissionsEnum.EDIT_CANNED_ANSWER}
          onRequestClose={onRequestClose}
        />
      </StyledFieldDiv>
      <StyledFieldDiv>
        <StyledBodyWrapper>{children}</StyledBodyWrapper>
      </StyledFieldDiv>
      {errorScope === 'global' ? errorNode : null}
    </OkCancelDialog>
  );
};

function getError(error: Error | undefined): [ErrorScope | null, ReactNode] {
  if (error === undefined) {
    return [null, null];
  }

  if (error instanceof AnswerNameAlreadyExistsError) {
    return ['name', <Trans id="answer-create-name-exist-error">This name is already used</Trans>];
  }

  return [
    'global',
    <StyledErrorDiv>
      <Trans id="answer-create-generic-error">An error occurred, please try again later.</Trans>
    </StyledErrorDiv>,
  ];
}
